import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_BASE_URL } from '../config';
import useDocumentTitle from '../hooks/useDocumentTitle';
import useFreeAccountStatus from '../hooks/useFreeAccountStatus';
import COUNTRIES, { findCountryByCode } from '../utils/countries';
import { captureAttribution, getAttribution } from '../utils/attribution';
import { trackAcquisitionEvent } from '../utils/acquisitionEvents';
import { getAppSearch } from '../utils/platform';
import { shareOffer } from '../utils/share';
import '../styles/BusinessRegistrationPage.css';

const FORM_STARTED_KEY = 'adwary_business_form_started';

function BusinessRegistrationPage() {
  const navigate = useNavigate();
  useDocumentTitle('سجّل كصاحب مصلحة');
  const { status: freeStatus, hasFreeLeft } = useFreeAccountStatus();

  const [form, setForm] = useState({ country: '', city: '', name: '', phone: '', category: '', categoryOther: '' });
  const [categories, setCategories] = useState([]);
  const [detecting, setDetecting] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    captureAttribution(getAppSearch());
    try {
      if (sessionStorage.getItem(FORM_STARTED_KEY)) return;
      sessionStorage.setItem(FORM_STARTED_KEY, 'true');
    } catch {
      // Tracking remains best-effort when storage is unavailable.
    }
    trackAcquisitionEvent('form_started', '/register-business');
  }, []);

  // Load the business categories offered in Adwary.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API_BASE_URL}/categories`);
        if (!cancelled && data && data.success) setCategories(data.categories || []);
      } catch {
        /* ignore — user can still pick "other" */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Auto-fill country (and city) from the visitor's IP; user can still change it.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('https://ipapi.co/json/');
        if (!res.ok) throw new Error('geo lookup failed');
        const geo = await res.json();
        if (cancelled) return;
        const match = findCountryByCode(geo.country_code);
        setForm((f) => ({
          ...f,
          country: match ? match.ar : (geo.country_name || f.country),
          city: f.city || geo.city || '',
        }));
      } catch {
        /* ignore — user selects manually */
      } finally {
        if (!cancelled) setDetecting(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error('يرجى إدخال الاسم');
    if (!form.phone.trim()) return toast.error('يرجى إدخال رقم الهاتف');
    const category = form.category === 'other'
      ? form.categoryOther.trim()
      : form.category.trim();
    if (form.category === 'other' && !category) {
      return toast.error('يرجى كتابة نوع النشاط');
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API_BASE_URL}/business-signup`, {
        name: form.name.trim(),
        phone: form.phone.trim(),
        country: form.country.trim(),
        city: form.city.trim(),
        category,
        ...getAttribution(),
      });
      if (data.success) {
        setSubmitted(true);
        trackAcquisitionEvent('signup_stored', '/register-business');
        toast.success(data.message || 'تم استلام طلبك بنجاح');
      } else {
        toast.error(data.message || 'حدث خطأ');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'حدث خطأ، يرجى المحاولة لاحقاً');
    } finally {
      setSubmitting(false);
    }
  };

  const handleShareOffer = async () => {
    trackAcquisitionEvent('offer_shared', '/register-business');
    const result = await shareOffer();
    if (result === 'copied') toast.success('تم نسخ رابط العرض');
    if (result === 'unavailable') toast.error('تعذر فتح المشاركة');
  };

  return (
    <div className="biz-reg-page">
      <div className="biz-reg-inner">
        <header className="biz-reg-brand">
          <img src="/icons/icon.svg" alt="أدواري" className="biz-reg-logo" />
        </header>

        {submitted ? (
          <section className="biz-reg-success">
            <div className="biz-reg-success-icon" aria-hidden="true">✓</div>
            <h1>تم استلام طلبك!</h1>
            <p>
              شكراً لاهتمامك بالانضمام إلى أدواري. سنراجع طلبك ونتواصل معك
              قريباً. يُمنح الحساب المجاني بعد قبول الطلب ما دام العرض متاحاً.
            </p>
            <div className="biz-reg-success-actions">
              <button type="button" className="btn-primary" onClick={handleShareOffer}>
                شارك العرض مع صاحب مصلحة آخر
              </button>
              <button type="button" className="biz-reg-secondary" onClick={() => navigate('/welcome')}>
                العودة للصفحة الرئيسية
              </button>
            </div>
          </section>
        ) : (
          <>
            <section className="biz-reg-hero">
              <h1 className="biz-reg-title">انضم إلى أدواري كصاحب مصلحة</h1>
              <div className={`biz-reg-free-banner ${freeStatus && !hasFreeLeft ? 'biz-reg-free-banner--full' : ''}`}>
                <span className="biz-reg-free-badge">عرض الإطلاق</span>
                <p className="biz-reg-free-text">
                  {freeStatus && hasFreeLeft
                    ? `متبقٍ ${freeStatus.remaining} من أصل ${freeStatus.total} — 6 أشهر مجاناً`
                    : freeStatus
                      ? 'اكتمل العرض المجاني — سجّل للانضمام إلى قائمة الانتظار'
                      : '6 أشهر مجاناً لأول 20 مصلحة'}
                </p>
              </div>
              <p className="biz-reg-desc">
                اطلب إنشاء حساب لصالونك أو مركز التجميل الخاص بك، وأدر حجوزاتك وعملاءك
                بكل سهولة. املأ البيانات التالية وسنتواصل معك لإنشاء الحساب.
              </p>
              <p className="biz-reg-offer-note">
                لا نطلب بيانات دفع. إرسال الطلب لا يحجز مكاناً؛ يُمنح العرض بعد مراجعة الطلب وقبوله.
              </p>
            </section>

            <form className="biz-reg-form" onSubmit={handleSubmit}>
              <div className="form-group">
                <label>الدولة <span>(اختياري)</span></label>
                <select value={form.country} onChange={update('country')}>
                  <option value="">
                    {detecting ? 'جاري تحديد موقعك...' : 'اختر الدولة'}
                  </option>
                  {COUNTRIES.map((c) => (
                    <option key={c.code} value={c.ar}>{c.ar}</option>
                  ))}
                  {form.country && !COUNTRIES.some((c) => c.ar === form.country) && (
                    <option value={form.country}>{form.country}</option>
                  )}
                </select>
              </div>

              <div className="form-group">
                <label>نوع النشاط <span>(اختياري)</span></label>
                <select value={form.category} onChange={update('category')}>
                  <option value="">اختر نوع النشاط</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.name_ar}>
                      {c.name_ar}
                    </option>
                  ))}
                  <option value="other">أخرى</option>
                </select>
              </div>

              {form.category === 'other' && (
                <div className="form-group">
                  <label>حدّد نوع النشاط</label>
                  <input
                    type="text"
                    value={form.categoryOther}
                    onChange={update('categoryOther')}
                    placeholder="اكتب نوع نشاطك التجاري"
                  />
                </div>
              )}

              <div className="form-group">
                <label>المدينة <span>(اختياري)</span></label>
                <input
                  type="text"
                  value={form.city}
                  onChange={update('city')}
                />
              </div>

              <div className="form-group">
                <label>الاسم *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={update('name')}
                  placeholder="اسمك أو اسم المصلحة"
                  required
                />
              </div>

              <div className="form-group">
                <label>رقم الهاتف *</label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={update('phone')}
                  placeholder="05XXXXXXXX"
                  dir="ltr"
                  required
                />
              </div>

              <button type="submit" className="btn-primary biz-reg-submit" disabled={submitting}>
                {submitting ? 'جاري الإرسال...' : 'اطلب الانضمام كصاحب مصلحة'}
              </button>
            </form>

            <div className="biz-reg-back">
              <Link to="/welcome" className="biz-reg-back-link">← العودة</Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default BusinessRegistrationPage;
